import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useMe } from '../../hooks/useMe.js';
import { apiRequest } from '../../lib/apiClient.js';
import {
  appleMobileDevice,
  devicePermission,
  openedAsApp,
  pushSupported,
  signOutThisDevice,
  signUpThisDevice,
  thisDeviceSubscription,
  type DevicePermission,
} from './pushDevice.js';

const pushStatusSchema = z.object({
  /** Null when notifications aren't set up on the server. */
  publicKey: z.string().nullable(),
  devices: z.number(),
});
type PushStatus = z.infer<typeof pushStatusSchema>;

/** "Not now" on the ask hides it this long, then it asks again. */
const ASK_AGAIN_AFTER_MS = 3 * 24 * 60 * 60_000;
const DISMISSED_KEY = 'push-ask-dismissed-at';
/** Set when its person switched notifications off here, so a reload doesn't sign it back up. */
const OFF_HERE_KEY = 'push-off-here';

const remember = (key: string, value: string | null): void => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Private window or blocked storage: the choice lasts until reload.
  }
};

const turnedOffHere = (): boolean => {
  try {
    return localStorage.getItem(OFF_HERE_KEY) !== null;
  } catch {
    return false;
  }
};

const dismissedRecently = (): boolean => {
  try {
    const at = Number(localStorage.getItem(DISMISSED_KEY));
    return Number.isFinite(at) && Date.now() - at < ASK_AGAIN_AFTER_MS;
  } catch {
    return false;
  }
};

export const usePushLogic = () => {
  const me = useMe();
  const queryClient = useQueryClient();
  const statusKey = ['push', me.data?.user.id];
  const status = useQuery({
    queryKey: statusKey,
    enabled: Boolean(me.data),
    queryFn: () => apiRequest<PushStatus>('/api/me/push', {}, pushStatusSchema),
  });
  // This browser's own state: the permission its person gave, and whether it's signed up.
  const device = useQuery({
    queryKey: ['push-device'],
    queryFn: async (): Promise<{ permission: DevicePermission; signedUp: boolean }> => ({
      permission: devicePermission(),
      signedUp: (await thisDeviceSubscription()) !== null,
    }),
  });
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: statusKey }),
      queryClient.invalidateQueries({ queryKey: ['push-device'] }),
    ]);

  const turnOn = useMutation({
    mutationFn: async (asked: Promise<NotificationPermission>) => {
      const publicKey = status.data?.publicKey;
      if (!publicKey) throw new Error('Notifications aren’t set up yet.');
      if ((await asked) !== 'granted') return;
      remember(OFF_HERE_KEY, null);
      const subscription = await signUpThisDevice(publicKey);
      await apiRequest('/api/me/push-subscriptions', { method: 'PUT', body: subscription });
    },
    onSettled: refresh,
  });
  const turnOff = useMutation({
    mutationFn: async () => {
      remember(OFF_HERE_KEY, '1');
      const endpoint = await signOutThisDevice();
      if (endpoint)
        await apiRequest('/api/me/push-subscriptions', { method: 'DELETE', body: { endpoint } });
    },
    onSettled: refresh,
  });
  const test = useMutation({
    mutationFn: () => apiRequest<{ sent: number }>('/api/me/push-test', { method: 'POST' }),
  });

  const [dismissed, setDismissed] = useState(dismissedRecently);
  const notNow = () => {
    remember(DISMISSED_KEY, String(Date.now()));
    setDismissed(true);
  };

  // Once allowed, every load re-confirms this browser's sign-up with the server, so a renewed
  // sign-up or a person new to an already-allowed computer keeps getting notified. Idempotent.
  useQuery({
    queryKey: ['push-sync', me.data?.user.id, status.data?.publicKey],
    enabled:
      Boolean(status.data?.publicKey) && devicePermission() === 'granted' && !turnedOffHere(),
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const subscription = await signUpThisDevice(status.data?.publicKey ?? '');
      await apiRequest('/api/me/push-subscriptions', { method: 'PUT', body: subscription });
      await queryClient.invalidateQueries({ queryKey: ['push-device'] });
      return true;
    },
  });

  const permission = device.data?.permission ?? devicePermission();
  const available = Boolean(status.data?.publicKey) && permission !== 'unsupported';
  // An iPhone or iPad only lets a site notify once it's on the Home Screen and opened from there.
  const homeScreenDevice =
    Boolean(status.data?.publicKey) && !pushSupported() && !openedAsApp()
      ? appleMobileDevice()
      : null;
  return {
    /** "iPhone" or "iPad" in a browser tab: it could notify from the Home Screen, not from here. */
    homeScreenDevice: dismissed ? null : homeScreenDevice,
    /** The server can send and this browser can show them. */
    available,
    permission,
    signedUp: device.data?.signedUp ?? false,
    /** This browser hasn't been asked yet, and wasn't told "not now" lately. */
    shouldAsk: available && permission === 'default' && !dismissed,
    // The prompt is asked for inside the click itself: Safari only shows it for something a
    // person just did, and the mutation runs a step later.
    turnOn: () => turnOn.mutate(Notification.requestPermission()),
    turnOff: () => turnOff.mutate(),
    isSwitching: turnOn.isPending || turnOff.isPending,
    error: turnOn.error ?? turnOff.error,
    notNow,
    sendTest: () => test.mutate(),
    test,
  };
};
